"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Wizard() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const body = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const regions = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.3
  });
  const img1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const img2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const img3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const glyph = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const partnerRegions = ["Precision Filtration", "Hydraulic Integrity", "Structural Casting", "Automated Balancing", "Thermal Regulation"];
  return <section data-section-id="3196" id="the-wizard-standard" className="relative overflow-hidden py-32 md:py-44 lg:py-[200px] bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div ref={glyph.ref} className="hidden lg:block absolute left-1/2 top-1/3 -translate-x-1/2 z-10 transition-all duration-1000 ease-out" style={{
          opacity: glyph.isVisible ? 0.6 : 0,
          transform: `translateX(-50%) rotate(${glyph.isVisible ? 0 : -45}deg) scale(${glyph.isVisible ? 1 : 0.5})`
        }}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-primary">
              <path d="M14 2 V26 M2 14 H26 M5 5 L23 23 M23 5 L5 23" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </div>

          {}
          <div className="lg:col-start-2 lg:col-span-5 flex flex-col">
            <div ref={eyebrow.ref} className="mb-8 transition-all duration-1000 ease-out" style={{
            opacity: eyebrow.isVisible ? 1 : 0,
            transform: eyebrow.isVisible ? "translateY(0)" : "translateY(20px)"
          }}>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
                Engineering — The Wizard Standard
              </span>
            </div>

            <h2 ref={heading.ref} className="font-sans font-semibold text-foreground text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter mb-12 transition-all duration-1000 ease-out delay-100" style={{
            opacity: heading.isVisible ? 1 : 0,
            transform: heading.isVisible ? "translateY(0)" : "translateY(30px)"
          }}>
              Built for{" "}
              <em className="not-italic font-medium text-primary">precision</em>{" "}
              where water meets architecture.
            </h2>

            <div ref={body.ref} className="space-y-6 transition-all duration-1000 ease-out delay-200" style={{
            opacity: body.isVisible ? 1 : 0,
            transform: body.isVisible ? "translateY(0)" : "translateY(20px)"
          }}>
              <p className="font-sans text-[18px] leading-[1.6] text-muted-foreground">A pool holds 80 tons of water for the next 40 years. We build it like that matters.</p>
              <p className="font-sans text-[18px] leading-[1.6] text-muted-foreground">Our installation philosophy is anchored in structural integrity, high-efficiency hydraulics, and automated control. Each component is selected for service life, not sticker price. Each pipe run is mapped to hold velocity in spec, because friction you design in is energy you pay for every month the pump runs.</p>
              <p className="font-sans text-[18px] leading-[1.6] text-muted-foreground">Twenty plus years of Army aviation taught one lesson that carries into every build: the details you skip are the ones that fail you later. That is why our spec is written before excavation, why every milestone is verified before the next begins, and why we will move a pour date rather than warranty a mistake.</p>
              <p className="font-sans text-[18px] leading-[1.6] text-muted-foreground">The result is not a marketing promise. It is what happens when the work is done methodically, in order, the first time.</p>
            </div>

            <p ref={regions.ref} className="font-mono text-[14px] leading-relaxed mt-12 text-foreground/80 transition-all duration-1000 ease-out delay-300" style={{
            opacity: regions.isVisible ? 1 : 0,
            transform: regions.isVisible ? "translateY(0)" : "translateY(20px)"
          }}>
              {partnerRegions.map((region, i) => <span key={i} data-index={i}>
                  {region}
                  {i < partnerRegions.length - 1 && <span className="inline-block mx-3 text-primary text-xs align-middle">
                      ●
                    </span>}
                </span>)}
            </p>
          </div>

          {}
          <div className="lg:col-start-7 lg:col-span-6 relative min-h-[900px]">
            {}
            <div ref={img1.ref} className="relative lg:w-[85%] transition-all duration-1000 ease-out" style={{
            opacity: img1.isVisible ? 1 : 0,
            transform: img1.isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.96)"
          }}>
              <div className="relative overflow-hidden border border-border" style={{
              borderRadius: "0px",
              aspectRatio: "4 / 5"
            }}>
                <img alt="Technician inspecting high-pressure pool filtration hardware" loading="lazy" decoding="async" className="w-full h-full object-cover" src="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/IMG_2151.jpg" />
                <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "3px 3px",
                opacity: 0.4
              }} />
              </div>
              <p className="font-mono text-xs mt-3 ml-2 text-muted-foreground">— SYSTEM: COMMERCIAL FILTRATION // SOLUTION (JOB #): 04117</p>
            </div>

            {}
            <div ref={img2.ref} className="relative lg:w-[55%] lg:ml-auto lg:-mt-20 mt-10 lg:mr-0 transition-all duration-1000 ease-out delay-150" style={{
            opacity: img2.isVisible ? 1 : 0,
            transform: img2.isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.96)"
          }}>
              <div className="relative overflow-hidden border border-border" style={{
              borderRadius: "0px",
              aspectRatio: "5 / 4"
            }}>
                <img alt="Crystal clear pool water surface with technical lighting" loading="lazy" decoding="async" className="w-full h-full object-cover" src="https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/65453385064__43A568AE-3E96-4BC2-BEB1-584E6F2075A5-scaled.webp" />
                <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "3px 3px",
                opacity: 0.4
              }} />
              </div>
              <p className="font-mono text-xs mt-3 ml-2 text-muted-foreground">
                — STATUS: OPTIMAL // PH: 7.4
              </p>
            </div>

            {}
            <div ref={img3.ref} className="relative lg:w-[68%] lg:mt-12 mt-10 transition-all duration-1000 ease-out delay-300" style={{
            opacity: img3.isVisible ? 1 : 0,
            transform: img3.isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.96)"
          }}>
              <div className="relative overflow-hidden border border-border" style={{
              borderRadius: "0px",
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_renovation_site.png" alt="Precision pool renovation site with structural reinforcement" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 pointer-events-none mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
                backgroundSize: "3px 3px",
                opacity: 0.4
              }} />
              </div>
              <p className="font-mono text-xs mt-3 ml-2 text-muted-foreground">
                — PROJECT: RENOVATION // ID: 88-B
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}